import type { ReactNode } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Badge } from '../ui/badge'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Textarea } from '../ui/textarea'
import { Input } from '../ui/input'
import { AttachmentsInput } from './AttachmentsInput'
import { LinkedProductsInput } from './LinkedProductsInput'
import { cn } from '../../lib/utils'

/**
 * The backend's `DynamicForm`/`DynamicFormField` shape (confirmed via
 * `oaq.py`, `FieldType`/`FieldVisibilityType` enums) — the same spec every
 * dynamic-form consumer fetches, whether scoped to a user
 * (`GET forms/users/{user_id}/{form_slug}`, `apps/specialists-portal`'s
 * `DynamicFormPanel`) or embedded on an event (`ExtendedEvent.form`, a
 * future `specialists/appointments/[event_id]` detail page per HATCH-356
 * review — see this file's own doc comment below).
 */
export interface DynamicFormFieldSpec {
  field_name: string
  label: string
  field_type: string
  required?: boolean | null
  choices?: unknown[][] | null
  visibility_type?: string | null
  /** A field's promo/highlight badge (`marketplace-frontend`'s `FieldRibbon`) — free-text, absent unless a form's own field definition sets one. */
  ribbon?: string | null
  /** Per-field opt-outs for the label/ribbon row and visibility badge this component renders by default — for a caller giving a field its own bespoke widget via `renderField`. */
  hideLabel?: boolean
  hideRibbon?: boolean
  hideVisibility?: boolean
  /** The backend's `FieldConfig.is_managed` (confirmed via `oaq.py`) — whether this field is actively managed (e.g. a `prescription` field gets the prescription builder). Not set or `false` means unmanaged; `EventForm`'s own companion-pair logic (HATCH-787) gates both a managed `X` field and its `X_products` sibling on this flag, checked on `X`. */
  field_config?: {
    is_managed?: boolean | null
  } | null
}

export interface DynamicFormSpec {
  form_id: string
  slug: string
  label: string
  description?: string | null
  show_header?: boolean | null
  submit_label?: string | null
  fields: DynamicFormFieldSpec[]
  /** The backend's own `DynamicForm.pure_contact_form` (confirmed via `oaq.py`) — a form that only collects contact fields, not a real booking/consultation intake; callers that need a genuine booking-details form should not accept one of these as a substitute. */
  pure_contact_form?: boolean | null
}

/**
 * `marketplace-frontend`'s own `FORM_FIELD_VISIBILITY_TYPE` labels
 * (`HatiDynamicFormVisibilityLabel.jsx`) — `regular` fields get no badge at
 * all (the common case), so this returns `null` for anything else.
 */
function visibilityLabel(visibilityType: string | null | undefined): { text: string; Icon: typeof Eye } | null {
  if (visibilityType === 'medical') return { text: 'Shared to patient', Icon: Eye }
  if (visibilityType === 'medical_only_doctor') {
    return { text: 'Internal — not shared with the patient', Icon: EyeOff }
  }
  return null
}

/** The visibility badge rendered per-field by default — exported so a caller with its own layout for a field it excludes from `form.fields` can still show it elsewhere. */
export function FieldVisibilityBadge({ visibilityType }: { visibilityType?: string | null }) {
  const visibility = visibilityLabel(visibilityType)
  if (!visibility) return null
  return (
    <span className="text-muted-foreground flex items-center gap-1 text-xs">
      <visibility.Icon className="size-3.5" />
      {visibility.text}
    </span>
  )
}

/**
 * One field's input, switched on `field_type` (`FieldType`, confirmed via
 * `oaq.py`) — the same reduced set `marketplace-frontend`'s
 * `HatiDynamicFormField.jsx` reference switches over, plus `attachments`
 * (`AttachmentsInput`, uploads via `POST /files/single`) and `linked_products`
 * (`LinkedProductsInput`, review on HATCH-358: "This logic must be
 * concentrated inside DynamicFormFieldInput - there is already logic there
 * which chooses how to render different types"). `esignature`
 * still has no case: no caller of this renderer needs it yet, and it would
 * need its own bespoke e-signature primitive this repo doesn't have yet
 * either — add a case here, not a per-caller fork, when a real caller needs
 * one.
 *
 * Exported so a `renderField` override that needs to attach extra content
 * right under a field's normal input (not replace it) — `EventForm`'s own
 * backward-compatibility notice under a fallback field's plain text input,
 * HATCH-714 — can still render this component's own switch instead of
 * reimplementing it.
 */
export function DynamicFormFieldInput({
  id,
  field,
  value,
  onChange,
  arrayValue,
  onArrayChange,
  disabled,
}: {
  id: string
  field: DynamicFormFieldSpec
  value: string
  onChange: (value: string) => void
  arrayValue: string[]
  onArrayChange: (value: string[]) => void
  disabled: boolean
}) {
  if (field.field_type === 'linked_products') {
    return (
      <fieldset disabled={disabled} className="contents">
        <LinkedProductsInput value={arrayValue} onChange={onArrayChange} />
      </fieldset>
    )
  }
  if (field.field_type === 'attachments') {
    return (
      <fieldset disabled={disabled} className="contents">
        <AttachmentsInput id={id} value={arrayValue} onChange={onArrayChange} />
      </fieldset>
    )
  }
  if (field.field_type === 'text_choice' && field.choices) {
    const choices = field.choices as [string, string][]
    return (
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {choices.map(([choiceValue, choiceLabel]) => (
            <SelectItem key={choiceValue} value={choiceValue}>
              {choiceLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }
  if (field.field_type === 'text') {
    return <Textarea id={id} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
  }
  return (
    <Input
      id={id}
      type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : field.field_type === 'email' ? 'email' : 'text'}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

/**
 * A `DynamicForm`'s fields as a plain, controlled grid — the shared
 * rendering piece every dynamic-form consumer in this repo needs, extracted
 * (HATCH-356 review: "extract common component, which can render
 * DynamicForm in different variations/configs") from
 * `apps/specialists-portal`'s original `DynamicFormPanel`, which fetched
 * *and* rendered in one place. Deliberately has no fetch/submit logic of its
 * own — those genuinely differ per real endpoint (`forms/users/{user_id}/
 * {form_slug}` for `DynamicFormPanel`'s user-scoped forms vs. the ticket's
 * own flagged `events/{event_id}/form`, a completely different body shape,
 * `EventForm`, for a future appointment-detail page) — same "fetch stays
 * with the caller, only rendering is shared" split `ResourceTable`/
 * `ResourceForm` themselves don't need (they own their own fetch through
 * `dataProvider`) but a bespoke-endpoint form like this one does.
 *
 * `values`/`onChange` are a plain controlled map (`Record<field_name,
 * string>`), not a `react-hook-form` form — every caller so far manages its
 * own edit-mode toggle and submit mutation around a plain `useState`, and
 * introducing a form library here would mean two different state models for
 * the same field set (the caller's own edit-mode/submit state, plus this
 * component's). If a future caller needs `react-hook-form` validation, wrap
 * this component's `values`/`onChange` around `Controller`, not fork it.
 */
export function DynamicFormRenderer({
  form,
  values,
  onChange,
  arrayValues,
  onArrayChange,
  disabled,
  columns = 2,
  className,
  renderField,
}: {
  form: DynamicFormSpec
  values: Record<string, string>
  onChange: (fieldName: string, value: string) => void
  /** `linked_products`/`attachments` fields' own array-valued state — a separate map from `values` since every other `field_type` here is a plain string. Omit if the form has no such field. */
  arrayValues?: Record<string, string[]>
  onArrayChange?: (fieldName: string, value: string[]) => void
  disabled: boolean
  /** Grid columns at the `md` breakpoint and up; single column below it. Defaults to 2 (this component's original two-column layout). */
  columns?: 1 | 2
  className?: string
  /**
   * Per-field input override, checked before this component's own
   * `DynamicFormFieldInput` switch — same "return `undefined` to fall back
   * to the default" convention `ResourceTable`'s own `renderField` prop
   * uses. For a `field_type` this component doesn't have a case for yet
   * (`esignature` — see this file's own doc comment on why
   * it isn't built in), or a caller-specific special case keyed by
   * `field_name` (e.g. a `prescription_products` field getting a dedicated
   * builder instead of the plain `linked_products` picker) — extend this
   * component with a real case when the input is generic; use this prop
   * when it's caller-specific.
   */
  renderField?: (
    field: DynamicFormFieldSpec,
    value: string,
    onChange: (value: string) => void,
    disabled: boolean,
  ) => ReactNode | undefined
}) {
  return (
    <div className={cn('grid grid-cols-1 gap-4', columns === 2 && 'md:grid-cols-2', className)}>
      {form.show_header ? (
        <div className="col-span-full flex flex-col gap-1">
          <h3 className="text-lg font-semibold">{form.label}</h3>
          {form.description ? <p className="text-muted-foreground text-sm">{form.description}</p> : null}
        </div>
      ) : null}
      {form.fields.map((field) => {
        const fieldId = `dynamic-form-${form.form_id}-${field.field_name}`
        const fieldValue = values[field.field_name] ?? ''
        const fieldOnChange = (value: string) => onChange(field.field_name, value)
        const overridden = renderField?.(field, fieldValue, fieldOnChange, disabled)
        const fieldArrayValue = arrayValues?.[field.field_name] ?? []
        const fieldArrayOnChange = (next: string[]) => onArrayChange?.(field.field_name, next)
        const showLabel = !field.hideLabel
        const showRibbon = Boolean(field.ribbon) && !field.hideRibbon
        return (
          <div key={field.field_name} className="flex flex-col gap-1.5">
            {showLabel || showRibbon ? (
              <div className="flex items-center gap-2">
                {showLabel ? (
                  <Label htmlFor={fieldId}>
                    {field.label}
                    {field.required ? <span className="text-destructive"> *</span> : null}
                  </Label>
                ) : null}
                {showRibbon ? (
                  <Badge variant="secondary" className="rounded-full bg-green-100 text-green-700 hover:bg-green-100">
                    {field.ribbon}
                  </Badge>
                ) : null}
              </div>
            ) : null}
            {overridden ?? (
              <DynamicFormFieldInput
                id={fieldId}
                field={field}
                value={fieldValue}
                onChange={fieldOnChange}
                arrayValue={fieldArrayValue}
                onArrayChange={fieldArrayOnChange}
                disabled={disabled}
              />
            )}
            {field.hideVisibility ? null : <FieldVisibilityBadge visibilityType={field.visibility_type} />}
          </div>
        )
      })}
    </div>
  )
}
